import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { FormDropdown, NotificationRuleRequestData, SampleMonitorEditRuleRequest } from '../interface/notification-data';
import { SampleTrackingStorage } from '../../sample-tracking-storage';
import { SampleTrackerData } from '../../group-info/study-group-info';
import { map, mergeMap, switchMap, publishReplay, refCount } from 'rxjs/operators';
import { Settings } from '../../../core';
import { HttpClient } from '@angular/common/http';
import { dataMember } from 'santee-dcts';
import { DateRangeFilterValue } from '../../../shared/time-frame-filter/timeframe-filter.component';

export class CriteriaReadModel {
    @dataMember()
    public criterias: any;
}

export class NotificationCountModel {
    @dataMember()
    public count: number[];
}

@Injectable()
export class SampleMonitoringStorage {

    public sampleMonitoringUrl = `${this.settings.apiUrl}/sponsor/sampletracker/rule`;
    private readonly newNotificationCount: Observable<number> = of(0);
    private loadNewNotificationCount$ = new BehaviorSubject<boolean>(true);


    constructor(
        private sampleTrackingStorage: SampleTrackingStorage,
        private settings: Settings,
        private http: HttpClient
    ) {
        this.newNotificationCount = this.loadNewNotificationCount$
            .pipe(
                mergeMap(() => of(this.getNewNotificationCount())),
                switchMap(x => x),
                publishReplay(1),
                refCount()
            );
    }

    public loadProtocols(): Observable<SampleTrackerData[]> {
        return this.sampleTrackingStorage.loadStudies().pipe(map((data => {
            if (data && data.length > 0) {
                data.forEach(t => {
                    t.key = t.protocolFk;
                    t.value = t.protocolCode;
                });
            }
            return data;
        })));
    }
    public forceLoadNotificationCount() {
        this.loadNewNotificationCount$.next(true);
    }

    public getNewNotificationCount(): Observable<number> {
        return this.http.get(`${this.sampleMonitoringUrl}/new/count`).pipe(
            map((rawData: NotificationCountModel) => {
                if (!rawData.count || rawData.count.length === 0) {
                    return 0;
                }
                return rawData.count[0];
            })
        );
    }

    public getFormDropDownData(): Observable<any> {
        return this.http.get(`${this.sampleMonitoringUrl}/notificationrulecriteria`).pipe(map(((result: CriteriaReadModel) => {
            return result.criterias;
        })));
    }

    public getNotificationRulesGridData(): Observable<any> {
        return this.http.get(`${this.sampleMonitoringUrl}/notificationrules`);
    }

    public createNewRule(data: NotificationRuleRequestData): Observable<any> {
        return this.http.post(`${this.sampleMonitoringUrl}/createrule`, data);
    }

    public ruleNameExists(data: any): Observable<any> {
        return this.http.post(`${this.sampleMonitoringUrl}/rulenameexists`, data);
    }

    public getRule(id: number): Observable<any> {
        return this.http.get(`${this.sampleMonitoringUrl}/${encodeURI(id.toString())}`)
            .pipe(map((result: any) => result.sampleTrackerEditRuleData[0]));
    }

    public updateRule(data: SampleMonitorEditRuleRequest): Observable<any> {
        return this.http.put(`${this.sampleMonitoringUrl}/update`, data);
    }

    public getRuleTypes(): Observable<any> {
        return this.http.get(`${this.sampleMonitoringUrl}/notificationruletype`).pipe(map((result: any) => {
            return result.ruleTypes;
        }));
    }

    public notificationMethod(data: { ruleId: string, emailNotification: string }): Observable<any> {
        return this.http.put(`${this.sampleMonitoringUrl}/toggleemail`, data);
    }

    public deleteRule(ruleId: string): Observable<any> {
        return this.http.delete(`${this.sampleMonitoringUrl}/deleterule/${ruleId}`);
    }

    public get notificationCount() {
        return this.newNotificationCount;
    }

    public getMonitoringNotificationsGridData(timeFrame: DateRangeFilterValue): Observable<any> {
        let requestData = {
            fromDate: timeFrame.dateBegin,
            toDate: timeFrame.dateEnd
        };
        return this.http.post(`${this.sampleMonitoringUrl}/notifications/list`, requestData).pipe(map((data: any) => {
            return data.notificationData;
        }));
    }

    public getMonitoringNotificationsStatus(): Observable<any> {
        return this.http.get(`${this.sampleMonitoringUrl}/status`)
            .pipe(map((data: any) => {
                return data.ruleData;
            }));
    }

    public getNotificationCriteria(eventType: number, protocol: FormDropdown) {
        let requestInfo = {
            eventType: eventType,
            sponsorName: protocol.sponsorName,
            protocolCode: protocol.value
        };
        return this.http.post(`${this.sampleMonitoringUrl}/notificationrulecriteria`, requestInfo).pipe(map(((result: CriteriaReadModel) => {
            return result.criterias;
        })));
    }
}